import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../../context/AuthContext';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const useCompanies = (showMessage) => {
  const [companies, setCompanies] = useState([]);
  // Starts true (not false) so the page-level skeleton is guaranteed to be
  // visible on first paint instead of racing the mount effect that flips it.
  const [loading, setLoading] = useState(true);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`${BASE_URL}/api/configurations/companies`, {
        method: 'GET',
      });
      const data = await response.json();
      setCompanies(data?.data || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      showMessage('error', 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const addCompany = async (companyName, quotas = {}) => {
    if (!companyName.trim()) {
      showMessage('error', 'Please enter a company name');
      return false;
    }

    try {
      const response = await apiFetch(`${BASE_URL}/api/configurations/companies/new`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName: companyName.trim(),
          maxEmployees: quotas?.maxEmployees ?? null,
          maxUsers: quotas?.maxUsers ?? null,
          codePolicy: quotas?.codePolicy ?? null,
        }),
      });

      if (!response.ok) {
        // 409 covers both "company already exists" AND "prefix already
        // used by another client" — the server's message distinguishes
        // them, so surface it as-is rather than assuming one meaning.
        const errBody = await response.json().catch(() => null);
        showMessage('error', errBody?.message || 'Failed to create company');
        return false;
      }

      showMessage('success', 'Company created successfully!');
      await fetchCompanies();
      return true;
    } catch (error) {
      console.error('Failed to create company:', error);
      showMessage('error', 'Failed to create company');
      return false;
    }
  };

  // Renames a client, cascading server-side to every table that stores
  // clientName (Employee, Department, AdminLevel, WorkflowDefinition,
  // CompanyConfiguration, etc. — see configurationService.renameClient).
  // This is the ONLY place clientName can ever change once employees exist
  // under it; every other edit path (employee edit forms, imports) has it
  // locked down specifically so it can only change here. Superadmin-only
  // route (enforced server-side too, not just by this UI being hidden from
  // other roles).
  const renameCompany = async (oldClientName, newClientName) => {
    const from = (oldClientName || '').trim();
    const to = (newClientName || '').trim();

    if (!to) {
      showMessage('error', 'Please enter a new client name');
      return false;
    }
    if (from.toLowerCase() === to.toLowerCase()) {
      showMessage('error', 'New client name must be different from the current one');
      return false;
    }

    try {
      const response = await apiFetch(`${BASE_URL}/api/configurations/rename-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldClientName: from, newClientName: to }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        showMessage('error', data?.message || 'Failed to rename client');
        return false;
      }

      showMessage('success', `Client renamed to "${to}" everywhere — employees, configurations, admin levels, workflows, and more.`);
      await fetchCompanies();
      return true;
    } catch (error) {
      console.error('Failed to rename client:', error);
      showMessage('error', 'Failed to rename client');
      return false;
    }
  };

  return { companies, loading, fetchCompanies, addCompany, renameCompany };
};